<article
  class="overflow-hidden rounded-xl border border-success/40 bg-white shadow-header z-10 relative"
  [attr.aria-label]="seal.documentDescription || sourceLabel"
>
  <header class="border-b px-5 py-4 flex items-start gap-3">
    <div class="flex-grow min-w-0">
      <div class="flex items-center gap-2 flex-wrap">
        <et-journal-seal-tag [seal]="seal"></et-journal-seal-tag>
        <h4 class="m-0 text-base font-bold text-midnight line-clamp-2">
          {{ seal.documentDescription || sourceLabel }}
        </h4>
      </div>
      <div
        class="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-light items-center"
      >
        <span class="inline-flex items-center gap-1.5">
          <span class="font-semibold uppercase tracking-wide">Act</span>
          <span class="text-midnight">{{ seal.notorialAct || '—' }}</span>
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span class="font-semibold uppercase tracking-wide"
            >Notarization date and time</span
          >
          <span class="text-midnight">{{
            seal.notarizationDate || seal.utcCreatedOn | date : 'medium'
          }}</span>
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span class="font-semibold uppercase tracking-wide"># Page</span>
          <span class="text-midnight">{{ seal.pageNumber || '—' }}</span>
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span class="font-semibold uppercase tracking-wide">Fee</span>
          <span class="text-midnight">{{
            seal.feeCharged | currency : 'USD' : 'symbol' : '1.2-2'
          }}</span>
        </span>
      </div>
    </div>
    <div class="flex items-center gap-2 shrink-0">
      @if (seal.holderEntry || seal.entries.length > 0) {
      <button
        type="button"
        class="btn btn-xs btn-outline normal-case rounded-full btn-error"
        (click)="onDeleteSeal()"
      >
        {{ isClearAction ? 'Clear' : 'Delete' }}
      </button>
      }
      <button
        type="button"
        class="btn btn-xs btn-outline normal-case rounded-full"
        (click)="onEditSeal()"
      >
        Edit
      </button>
      <!-- Preview pdf button -->
      <et-journal-preview-pdf-btn [seal]="seal"></et-journal-preview-pdf-btn>
    </div>
  </header>

  <div class="flex flex-col gap-5 p-5">
    <section class="relative">
      <p
        class="m-0 mb-3 text-xs font-semibold uppercase tracking-wide text-light"
      >
        Document signers
      </p>
      @if (isUpdatingSigners()) {
      <div
        class="absolute inset-0 z-10 flex items-center justify-center bg-white/70"
      >
        <et-atoms-spinner size="small"></et-atoms-spinner>
      </div>
      }
      <div
        class="flex flex-wrap gap-4"
        [class.pointer-events-none]="isUpdatingSigners()"
        [class.opacity-50]="isUpdatingSigners()"
      >
        @for (signer of signers; track signer.eJournalSignerId) { @let selected
        = isSignerSelected(signer.eJournalSignerId);
        <div class="flex flex-col items-center gap-1 w-24 text-center">
          <button
            type="button"
            (click)="onToggleSignerSelection(signer.eJournalSignerId)"
            [disabled]="isUpdatingSigners()"
            class="flex h-8 w-8 items-center justify-center rounded-md border"
            [class.border-success]="selected"
            [class.bg-success]="selected"
            [class.text-white]="selected"
            [class.border-base-300]="!selected"
            [attr.aria-label]="
              selected
                ? signer.name + ' assigned'
                : signer.name + ' not assigned'
            "
          >
            @if (selected) {
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-4 h-4"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                clip-rule="evenodd"
              />
            </svg>
            }
          </button>
          <span
            class="text-xs line-clamp-2 leading-tight"
            [class.text-success]="selected"
            [class.font-semibold]="selected"
            [class.text-light]="!selected"
          >
            {{ signer.name }}
          </span>
        </div>
        } @empty {
        <p class="m-0 text-sm text-light">No signers on this journal yet</p>
        }
      </div>
    </section>

    @if (seal.notes) {
    <section>
      <p
        class="m-0 mb-2 text-xs font-semibold uppercase tracking-wide text-light"
      >
        Notes
      </p>
      <p class="m-0 text-sm text-midnight whitespace-pre-line line-clamp-4">
        {{ seal.notes }}
      </p>
    </section>
    }
  </div>
</article>

<ng-template #deleteSealModal>
  <div class="flex flex-col gap-6">
    <div class="flex items-center justify-between">
      <h3 class="flex items-center gap-3 text-lg font-bold">
        {{ isClearAction ? 'Clear eSeal' : 'Delete Seal' }}
      </h3>
      <button class="self-start" mat-dialog-close>
        <img src="assets/icons/close.svg" alt="Close" width="16" height="16" />
      </button>
    </div>
    <div>
      @if (seal.source === 'seal') {
      <p>
        Are you sure you want to delete
        <strong>{{ seal.documentDescription || 'this seal' }}</strong
        >? This removes all journal entries for the assigned signers.
      </p>
      <p>This action cannot be undone.</p>
      } @else {
      <p>
        Are you sure you want to clear
        <strong>{{ seal.documentDescription || 'this eSeal' }}</strong
        >? This removes all journal entries for the assigned signers.
      </p>
      }
    </div>
    <div class="flex justify-center items-center gap-4">
      <button
        matDialogClose
        class="normal-case h-10 min-h-[40px] rounded-full btn btn-outline min-w-[160px] text-base"
      >
        Cancel
      </button>
      <button
        (click)="deleteSeal()"
        class="normal-case h-10 min-h-[40px] rounded-full btn btn-primary min-w-[160px] text-base"
      >
        @if (isDeletingSeal()) {
        <et-atoms-spinner size="small"></et-atoms-spinner>
        } @else { {{ isClearAction ? 'Clear' : 'Delete' }} }
      </button>
    </div>
  </div>
</ng-template>
